import {ChatMessageType, toDate, UserChatMessage} from "services";
import {Apps, Genders} from "enums";
import {toDateString} from "utils";
import React, {FC, useContext, useEffect, useRef, useState} from "react";
import {AuthContext} from "contexts";
import Highlighter from "react-highlight-words";
import {FaAngleDoubleDown, FaAngleDoubleUp} from "react-icons/fa";
import {
    ModerationChatAIAnalysis
} from "components/ModerationUserCard/components/ModerationChats/ModerationChatAIAnalysis";

interface Props {
    userKey: string
    userName: string
    userGender: Genders
    messages: UserChatMessage[]
    isMyMessageInverted?: boolean
    abuseWords?: string[]
    hasAbuseJumps?: boolean
}

export const ModerationChat: FC<Props> = ({
                                              abuseWords,
                                              userName,
                                              messages,
                                              isMyMessageInverted,
                                              hasAbuseJumps,
                                              userGender,
                                              userKey
                                          }) => {
    const {env} = useContext(AuthContext);
    const messageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const [abuseMessageIndexes, setAbuseMessageIndexes] = useState<number[]>([]);
    const [currentAbuseIndex, setCurrentAbuseIndex] = useState<number>(-1);

    const getContent = (messageType: ChatMessageType, message: string) => {
        switch (messageType) {
            case ChatMessageType.Text:
                return message;
            case ChatMessageType.Audio:
                return "🔊Аудио";
            case ChatMessageType.Image:
                return "📷 Фото " + message;
            case ChatMessageType.SelfDestructingImage:
                return "📷 Самоудаляющееся Фото " + message;
            case ChatMessageType.ScamWarning:
                return `⚠️ Системное предупреждение о скаме за "${message}"`;
            case ChatMessageType.ScreenshotWarning:
                return "⚠️ Сделал скриншот, собеседник получил предупреждение";
            case ChatMessageType.Icebreaker:
                return `Ice-Breaker: ${message}`;
            case ChatMessageType.IcebreakerAnswer:
                return `Ice-Breaker Ответ: ${message}`;
            case ChatMessageType.Greeting:
                return `❤️‍🔥 Суперлайк ${message ? `: ${message}` : ''}`;
            default:
                return `${messageType.toString()}: ${message}`;
        }
    }

    const getClassName = (messageType: ChatMessageType) => {
        switch (messageType) {
            case ChatMessageType.Text:
            case ChatMessageType.Greeting:
            case ChatMessageType.Icebreaker:
            case ChatMessageType.IcebreakerAnswer:
                return "";
            case ChatMessageType.Audio:
            case ChatMessageType.Image:
            case ChatMessageType.SelfDestructingImage:
                return "text-info";
            case ChatMessageType.ScamWarning:
            case ChatMessageType.ScreenshotWarning:
                return "text-bg-danger";
            default:
                return "";
        }
    }

    // Check if a message contains abuse words
    const hasAbuseWords = (message: UserChatMessage): boolean => {
        if (!abuseWords || abuseWords.length === 0 || message.messageType !== ChatMessageType.Text) {
            return false;
        }

        const content = getContent(message.messageType, message.message);
        return abuseWords.some(word =>
            content.toLowerCase()?.includes(word.toLowerCase())
        );
    };

    // Check if a message is suspicious (abuse words or specific message types)
    const isSuspiciousMessage = (message: UserChatMessage): boolean => {
        return hasAbuseWords(message) ||
            message.messageType === ChatMessageType.ScreenshotWarning ||
            message.messageType === ChatMessageType.ScamWarning;
    };

    // Go to next suspicious message
    const goToNextSuspicious = () => {
        if (abuseMessageIndexes.length === 0) return;

        const nextIndex = (currentAbuseIndex + 1) % abuseMessageIndexes.length;
        setCurrentAbuseIndex(nextIndex);

        const messageIndex = abuseMessageIndexes[nextIndex];
        if (messageRefs.current[messageIndex]) {
            messageRefs.current[messageIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    };

    // Go to previous suspicious message
    const goToPrevSuspicious = () => {
        if (abuseMessageIndexes.length === 0) return;

        const prevIndex = (currentAbuseIndex - 1 + abuseMessageIndexes.length) % abuseMessageIndexes.length;
        setCurrentAbuseIndex(prevIndex);

        const messageIndex = abuseMessageIndexes[prevIndex];
        if (messageRefs.current[messageIndex]) {
            messageRefs.current[messageIndex]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    };

    const goToAbuse = (word: string, indices: number[]) => {
        const messageIndex = messages.findIndex(m => m.message?.includes(word));
        const abuseIndex = indices.findIndex(i => i === messageIndex);

        setCurrentAbuseIndex(abuseIndex)

        messageRefs.current[messageIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
        });
    }

    useEffect(() => {
        if (!messages || messages.length === 0) {
            setAbuseMessageIndexes([]);
            setCurrentAbuseIndex(-1);
            return;
        }

        const indices = messages
            .map((message, index) => isSuspiciousMessage(message) ? index : -1)
            .filter(index => index !== -1);

        setAbuseMessageIndexes(indices);
        setCurrentAbuseIndex(indices.length > 0 ? 0 : -1);

        if (abuseWords?.length) {
            goToAbuse(abuseWords[0], indices)
        }


    }, [messages, abuseWords]);

    return (
        <>
            {hasAbuseJumps && abuseMessageIndexes.length > 0 && (
                <div
                    className="sticky-top d-flex flex-row justify-content-between w-100 py-2 px-2 border-bottom bg-white">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center text-black"
                        onClick={goToPrevSuspicious}
                        disabled={abuseMessageIndexes.length <= 1}
                    >
                        <FaAngleDoubleUp size={14} style={{marginRight: 4}}/>
                        Предыдущее
                    </button>

                    <span className="text-muted small d-flex align-items-center">
                        {currentAbuseIndex !== -1 ? `${currentAbuseIndex + 1}/${abuseMessageIndexes.length}` : '0/0'}
                        подозрительных сообщений
                    </span>

                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-flex flex-row align-items-center text-black"
                        onClick={goToNextSuspicious}
                        disabled={abuseMessageIndexes.length <= 1}
                    >
                        Следующее
                        <FaAngleDoubleDown size={14} style={{marginLeft: 4}}/>
                    </button>
                </div>
            )}

            <div>
                <ModerationChatAIAnalysis userKey={userKey} userGender={userGender} userName={userName}
                                          messages={messages}/>
                {messages?.map((message, i) => {
                    const content = getContent(message.messageType, message.message);
                    const date = toDate(message.date, env?.app ?? Apps.Instinct);
                    const dateString = date ? `(${toDateString(date, false)})` : '';
                    const isMyMessage = isMyMessageInverted ? !message.isMyMessage : message.isMyMessage;
                    const containsAbuseWords = hasAbuseWords(message);
                    const isSuspicious = isSuspiciousMessage(message);
                    const isCurrentSuspicious = abuseMessageIndexes[currentAbuseIndex] === i;

                    return (
                        <div
                            key={message.date + i}
                            ref={el => messageRefs.current[i] = el}
                            className={`mb-1 ${isMyMessage ? 'text-dark' : 'text-body'} 
                                      ${containsAbuseWords ? 'abuse-message' : ''} 
                                      ${isSuspicious && isCurrentSuspicious ? 'current-suspicious-message' : ''}`}
                        >
                            <span className={isMyMessage ? `fw-semibold` : ''}>
                                {isMyMessage ? userName : 'Собеседник'} {dateString}:{' '}
                                {abuseWords && message.messageType === ChatMessageType.Text ? (
                                    <Highlighter
                                        highlightClassName={"bg-warning"}
                                        searchWords={abuseWords}
                                        autoEscape={true}
                                        textToHighlight={content}
                                    />
                                ) : (
                                    <span className={getClassName(message.messageType)}>{content}</span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </>
    );
}